import UserAPI from "@api/User.api";
import type { IClaim } from "@api/models/IClaim";
import GradientBorder from "@core-components/gradient-border/GradientBorder";
import SSPagination from "@core-components/pagination/Pagination";
import SSWhiteText from "@core-components/title/components/SSWhiteText";
import useAppTranslation from "@hooks/language/useAppTranslation";
import { Flex, Group, Modal, Stack, Table, Text } from "@mantine/core";
import useRewardModalStore from "@store/rewardModal.store";
import { IconArrowRight } from "@tabler/icons-react";
import { type ReactNode, useEffect, useState } from "react";

interface IRewardHistoryProps {
  opened: boolean;
  onClose: () => void;
}

const FIRST_PAGE_INDEX = 1;

const RewardHistoryModal: React.FC<IRewardHistoryProps> = ({
  opened,
  onClose,
}) => {
  const { open } = useRewardModalStore();
  const [rewards, setRewards] = useState<IClaim[]>([]);

  const [pagination, setPagination] = useState({
    current: FIRST_PAGE_INDEX,
    total: 0,
    itemsPerPage: 0,
    pages: 0,
  });

  useEffect(() => {
    if (opened) {
      getRewards(FIRST_PAGE_INDEX);
    }
  }, [opened]);

  const getRewards = (page: number) => {
    UserAPI.getRewardClaims(page).then((res) => {
      setRewards(res.data);
      setPagination({
        current: res.meta.currentPage,
        itemsPerPage: res.meta.perPage,
        total: res.meta.total,
        pages: Math.ceil(res.meta.total / res.meta.perPage),
      });
    });
  };

  const onPaginate = (current: number) => {
    setPagination((old) => ({ ...old, current }));
    getRewards(current);
  };

  const handleClose = () => {
    onClose();
    setTimeout(() => {
      setRewards([]);
    }, 400);
  };

  const { t } = useAppTranslation("pages.clientDashboard.components");
  const { t: tRewards } = useAppTranslation();

  const rows = rewards.map((reward) => (
    <Table.Tr
      key={`${reward.i18nKey}-${reward.date.millisecond()}`}
      className="border-b-0 font-inter font-semibold"
    >
      <Table.Td className="text-center">
        {tRewards(`${reward.i18nKey}.label`)}
      </Table.Td>
      <Table.Td className="text-center">
        {reward.date.format("DD/MM/YYYY")}
      </Table.Td>
      <Table.Td className="text-center">
        <GradientBorder
          className="gradient-gray-to-light-white hover-scale rounded-[4px]"
          childrenClassName="p-1 py-[3px] !bg-[#202125]"
          onClick={() => open(reward)}
        >
          <Group gap={2} wrap="nowrap" justify="center">
            <Text className="m-0 p-0 text-xs">
              {t("rewardHistoryModal.viewReward")}
            </Text>
            <IconArrowRight size={20} />
          </Group>
        </GradientBorder>
      </Table.Td>
    </Table.Tr>
  ));

  const Th = ({
    children,
    className,
  }: { children: ReactNode; className?: string }) => {
    return (
      <Table.Th
        className={`border-primary-light border-b-[1px] border-solid text-center font-normal text-sm ${className}`}
      >
        {children}
      </Table.Th>
    );
  };

  const noContent = (
    <Flex
      justify="center"
      align="center"
      className="sticky left-0 h-[150px] bg-primary-client text-center text-white"
    >
      {t("rewardHistoryModal.noContent")}
    </Flex>
  );

  return (
    <Modal
      centered
      zIndex={200}
      classNames={{
        body: "p-0 pb-4 xs:py-4 xs:px-2 md:p-4 bg-primary-client",
        header: "bg-primary-default shadow-lg",
        title: "w-full text-center",
        content: "bg-primary-background select-none rounded-lg",
      }}
      closeButtonProps={{ className: "default-icon" }}
      opened={opened}
      title={
        <SSWhiteText className="font-bold font-secret text-xl" span={false}>
          {t("rewardHistoryModal.title")}
        </SSWhiteText>
      }
      onClose={handleClose}
    >
      <Stack>
        <div className="overflow-auto">
          <Table className="border-separate rounded-md border-[1px] border-primary-light border-solid bg-primary-client text-primary-white">
            <Table.Thead className="h-14 bg-primary-background text-primary-dark-text">
              <Table.Tr>
                <Th className="rounded-tl-rounded-md">
                  {t("rewardHistoryModal.headers.reward")}
                </Th>
                <Th>{t("rewardHistoryModal.headers.date")}</Th>
                <Th className="rounded-tr-rounded-md">
                  {t("rewardHistoryModal.headers.informations")}
                </Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>{rows}</Table.Tbody>
            {!rows.length && <Table.Td colSpan={3}>{noContent}</Table.Td>}
          </Table>
        </div>

        <SSPagination
          currentPage={pagination.current}
          itemsCount={pagination.total}
          itemsPerPage={pagination.itemsPerPage}
          pageCount={pagination.pages}
          onPaginate={onPaginate}
        />
      </Stack>
    </Modal>
  );
};

export default RewardHistoryModal;
